// „Rechnung berichtigen“ (SPEZIFIKATION §4.14): Ist eine Pflichtangabe einer Rechnung falsch oder fehlt sie, ersetzt
// eine berichtigte Rechnung (E-Rechnung Typ 384 mit Bezug auf die ursprüngliche) die alte – auch wenn sie schon bezahlt
// ist. Zwei Schritte:
//   1. finance.invoice.rectify       legt NUR einen Entwurf an (Kopie der Rechnung). Nichts wird storniert; die
//                                    Rechnung und ihre E-Rechnung bleiben gültig. Verwerfen des Entwurfs lässt nichts zurück.
//   2. finance.rectification.post    in EINER Transaktion: Storno der alten Rechnung ohne eigene E-Rechnung (auch wenn
//                                    sie bezahlt ist), Festschreiben der berichtigten, Guthaben des Stornos verrechnet.
import { defineContract } from '@shared/contract'
import { zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zDay, zReason } from './common'
import { INVOICE_PERMISSIONS } from './invoice'

/** Art der Aufgabe „Berichtigung offen“ (am Entwurf der berichtigten Rechnung) */
export const RECTIFICATION_TASK = 'finance.invoice.rectification'

export const invoiceRectify = defineContract({
  name: 'finance.invoice.rectify',
  title: 'Rechnung berichtigen',
  description:
    'Legt den Entwurf einer berichtigten Rechnung an – eine Kopie der Rechnung, die sie beim Festschreiben ersetzt. Bis dahin bleibt die Rechnung mit ihrer E-Rechnung gültig.',
  llm: {
    description:
      'Legt zu einer festgeschriebenen Rechnung den ENTWURF einer berichtigten Rechnung an (E-Rechnung Typ 384): eine ' +
      'Kopie mit Kopf, Positionen und denselben Lieferscheinbezügen, Rechnungsdatum heute. Verwenden, wenn eine ' +
      'Pflichtangabe falsch ist oder fehlt (Anschrift, USt-IdNr., Steuersatz, Leistungsdatum) – auch bei bezahlten ' +
      'Rechnungen. Es wird NICHTS storniert: Den Entwurf bei Bedarf mit finance.invoice.saveDraft ändern (Stammdaten wie ' +
      'die Anschrift vorher im Kunden berichtigen), dann mit finance.rectification.post festschreiben – erst dabei wird ' +
      'die alte Rechnung storniert. Für Preisnachlässe ohne falsche Pflichtangabe: finance.creditNote.correct; ganz ' +
      'falsche Rechnung (anderer Kunde, doppelt): finance.invoice.reverse.',
    keywords: ['Rechnung berichtigen', 'berichtigte Rechnung', 'Rechnung ersetzen', 'falsche Anschrift', 'Pflichtangabe', 'Berichtigung'],
  },
  input: z.strictObject({
    /** die festgeschriebene Rechnung, die ersetzt werden soll */
    id: zId,
    /** was an der Rechnung falsch ist – steht im Protokoll, an der Aufgabe und am Storno */
    reason: zReason,
    /** Datum der berichtigten Rechnung; Standard: heute */
    documentDate: zDay.optional(),
  }),
  output: zDocumentView,
  permission: INVOICE_PERMISSIONS.write,
  risk: 'write',
  scope: 'record',
  effects: { reversible: true },
  fieldPermissions: { 'lines.*.costCents': 'costs.read' },
})

export const rectificationPost = defineContract({
  name: 'finance.rectification.post',
  title: 'Berichtigte Rechnung festschreiben',
  description:
    'Schreibt die berichtigte Rechnung fest und storniert dabei die ursprüngliche – auch wenn sie bezahlt ist. Das Storno bekommt keine eigene E-Rechnung; was bezahlt war, wird mit der berichtigten Rechnung verrechnet.',
  llm: {
    description:
      'Schreibt den Entwurf einer berichtigten Rechnung (aus finance.invoice.rectify) fest. In EINEM Schritt: Die ' +
      'ursprüngliche Rechnung wird storniert (ohne eigene E-Rechnung; auch wenn sie bezahlt oder verrechnet ist), die ' +
      'berichtigte Rechnung bekommt Nummer, offenen Posten und – wo nötig – ihre E-Rechnung (Typ 384 mit Bezug auf die ' +
      'alte), und das Guthaben aus dem Storno wird mit ihr verrechnet; ein Rest bleibt Guthaben des Kunden. Nicht ' +
      'umkehrbar. Nur für Entwürfe aus finance.invoice.rectify – alle anderen Rechnungen mit finance.invoice.post.',
    keywords: ['berichtigte Rechnung festschreiben', 'Berichtigung buchen', 'Rechnung ersetzen'],
  },
  input: z.strictObject({
    /** Entwurf der berichtigten Rechnung */
    id: zId,
    /** Version des geladenen Entwurfs (optimistische Sperre) */
    version: z.int().optional(),
  }),
  output: zDocumentView,
  permission: INVOICE_PERMISSIONS.write,
  risk: 'financial',
  scope: 'record',
  fieldPermissions: { 'lines.*.costCents': 'costs.read' },
})
